import { CheckCircle, NavigateBefore } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import useApi from "_api/index";
import CustomizeDialog from "components/Car/CustomizeDialog";
import RenderFieldsCars from "components/Car/RenderFieldsCars";
import RenderFieldsCustomers from "components/Car/RenderFieldsCustomers";
import {
  GradientButton,
  GradientButtonOutline,
  GradientShadow,
} from "components/GradientButton";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import InvoicePreview from "./InvoicePreview";
import ReceiptHeader from "components/Form/ReceiptHeader";
import TableProductReceipt from "components/Form/TableProductReceipt";
import {
  formatCurrency2Digit,
  formatDateMonthYear,
} from "utils/helpers/format";
import ReceiptFooter from "components/Form/ReceiptFooter";

const ModalPawnCar = ({ open, onClose, car, rowID, isEdit, fetchData }) => {
  const [customerImage, setCustomerImage] = useState([]);
  const [carImages, setCarImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);

  const generateInitialValues = () => {
    return {
      // ข้อมูลลูกค้า
      prefix: "",
      fullName: "",
      idCardNumber: "",
      phoneNumber: "",
      address: "",
      nationality: "ไทย",

      // ข้อมูลรถ
      car_name: "",
      car_type: "", // "รถยนต์", "มอเตอร์ไซด์"
      car_no: "",
      license_plate: "",
      license_province: "",
      license_date: "",
      tax_expire_date: "",
      compulsory_expire_date: "",
      characteristic: "",
      car_brand_id: null,
      color: "",
      model: "",
      fuel_type: "",
      engine_brand: "",
      engine_number: "",
      gas_number: null,
      number_of_engine_cylinders: null,
      cc: null,
      horsepower: null,
      weight: null,
      seat: null,
      car_mileage: "",
      car_price: "",
      insurance_company: "" || null,
      insurance_level: "" || null,
      insurance_expire_date: "" || null,
      purchase_type: "cash", // "cash", "installment"
      defect: "" || null,
      car_status_id: 1,
      note: "" || null,
      car_product_type: "pawn_car",

      // ข้อมูลการขายฝาก
      total_month: 0,
      interest_rate: 0,
    };
  };

  const generateValidationSchema = () => {
    return Yup.object({
      // ข้อมูลลูกค้า
      prefix: Yup.string().required("กรุณาเลือกคำนำหน้า"),
      fullName: Yup.string().required("กรุณากรอกชื่อ-นามสกุล"),
      idCardNumber: Yup.string().when("nationality", {
        is: (val) => val === "ไทย",
        then: (schema) =>
          schema
            .required("กรุณากรอกเลขบัตรประชาชน")
            .matches(/^\d+$/, "เลขบัตรประชาชนต้องเป็นตัวเลขเท่านั้น")
            .length(13, "เลขบัตรประชาชนต้องมี 13 หลัก"),
        otherwise: (schema) =>
          schema
            .required("กรุณากรอกเลขหนังสือเดินทาง (Passport No.)")
            .matches(
              /^[A-Za-z0-9]+$/,
              "เลขหนังสือเดินทางต้องเป็นตัวอักษรหรือตัวเลขเท่านั้น"
            )
            .min(6, "เลขหนังสือเดินทางต้องมีอย่างน้อย 6 หลัก")
            .max(20, "เลขหนังสือเดินทางกรอกได้มากสุด 20 หลัก"),
      }),
      phoneNumber: Yup.string()
        .required("กรุณากรอกเบอร์โทรศัพท์")
        .min(9, "เบอร์โทรศัพท์ต้องมีอย่างน้อย 9 หลัก")
        .max(10, "เบอร์โทรศัพท์ต้องไม่เกิน 10 หลัก")
        .matches(/^\d+$/, "เบอร์โทรศัพท์ต้องเป็นตัวเลขเท่านั้น"),
      address: Yup.string().required("กรุณากรอกที่อยู่"),
      nationality: Yup.string().required("กรุณาเลือกสัญชาติ"),

      car_name: Yup.string().required("กรุณากรอกรถ"),
      car_type: Yup.string().required("กรุณาเลือกประเภทของรถ"),
      car_no: Yup.string().required("กรุณากรอกเลขตัวถังรถ"),
      license_plate: Yup.string().required("กรุณากรอกหมายเลขทะเบียนรถ"),
      license_province: Yup.string().required("กรุณาเลือกจังหวัดทะเบียนรถ"),
      license_date: Yup.date().required("กรุณาเลือกวันที่ออกทะเบียนรถ"),
      tax_expire_date: Yup.date().required("กรุณาเลือกวันหมดอายุภาษี"),
      compulsory_expire_date: Yup.date().required(
        "กรุณาเลือกวันหมดอายุ พ.ร.บ."
      ),
      characteristic: Yup.string().required("กรุณากรอกรายละเอียดรถ"),
      car_brand_id: Yup.number().required("กรุณาเลือกยี่ห้อรถ"),
      color: Yup.string().required("กรุณาเลือกสีรถ"),
      model: Yup.string().required("กรุณากรอกรุ่นรถ"),
      fuel_type: Yup.string().required("กรุณาเลือกประเภทเชื้อเพลิง"),
      engine_brand: Yup.string().required("กรุณากรอกยี่ห้อเครื่องยนต์"),
      engine_number: Yup.string().required("กรุณากรอกหมายเลขเครื่องยนต์"),
      number_of_engine_cylinders: Yup.number().required(
        "กรุณากรอกจำนวนกระบอกสูบ"
      ),
      cc: Yup.number().required("กรุณากรอกความจุกระบอกสูบ (CC)"),
      horsepower: Yup.number().required("กรุณากรอกแรงม้า"),
      weight: Yup.number().required("กรุณากรอกน้ำหนักรถ"),
      seat: Yup.number().required("กรุณากรอกจำนวนที่นั่ง"),
      car_mileage: Yup.number().required("กรุณากรอกระยะทางที่ใช้ไป (กิโลเมตร)"),
      car_price: Yup.number()
        .required("กรุณากรอกราคาจำนำรถ")
        .min(1, "กรุณากรอกราคาจำนำรถ"),
      purchase_type: Yup.string().required("กรุณาเลือกประเภทการชำระเงิน"),

      total_month: Yup.number()
        .required("กรุณากรอกระยะเวลาในการขายฝาก (เดือน)")
        .min(1, "ระยะเวลาขายฝากต้องไม่น้อยกว่า 1 เดือน"),
      interest_rate: Yup.number()
        .required("กรุณากรอกอัตราดอกเบี้ย")
        .min(0, "กรุณากรอกอัตราดอกเบี้ย"),
    });
  };

  const formik = useFormik({
    initialValues: generateInitialValues(),
    validationSchema: generateValidationSchema(),
    onSubmit: (values) => {
      handleCreatePawnCar(values);
    },
  });

  const handleCreatePawnCar = async (values) => {
    try {
      const data = {
        car_data: {
          ...values,
          car_product_type: "pawn_car",
        },
        customer_data: {
          prefix: values.prefix,
          fullName: values.fullName,
          idCardNumber: values.idCardNumber,
          phoneNumber: values.phoneNumber,
          address: values.address,
          nationality: values.nationality || "ไทย",
          customer_type: "car",
        },
        order_data: {
          total_month: values.total_month,
          interest_rate: values.interest_rate,
        },
        car_photos: carImages,
        image: customerImage,
      };

      const response = await useApi.pawn_car.createOrderPawnCar(data);
      console.log("Response from createOrderPawnCar:", response);
      if (response) {
        console.log("Pawn car created successfully:", response);
        setIsSuccess(true);
        // onClose(); // Close the dialog after successful creation
      }
    } catch (error) {
      console.error("Error creating pawn car:", error);
      // onError(error.message);
    }
  };

  useEffect(() => {
    formik.resetForm({
      values: generateInitialValues(),
    });
    setCustomerImage([]);
    setCarImages([]);
    setActiveStep(0);
    setIsSuccess(false);
  }, [open]);

  // start card reader
  // start websocket
  const [intendPresentFlag, setIntendPresentFlag] = useState(true);
  let wSocket = null;
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };
  const setAutoReadOptions = () => {
    const cmdNIDAutoRead = document.getElementById("chkAutoRead").checked;
    const cmdIDNumber = document.getElementById("chkAutoNIDNumber").checked;
    const cmdText = document.getElementById("chkAutoNIDText").checked;
    const cmdphoto = document.getElementById("chkAutoNIDPhoto").checked;
    const JS_OBJ = {
      Command: "SetAutoReadOptions",
      AutoRead: cmdNIDAutoRead,
      IDNumberRead: cmdIDNumber,
      IDTextRead: cmdText,
      IDATextRead: true,
      IDPhotoRead: true,
    };
    console.log("JS_OBJ >>> ", JS_OBJ);
    var jsonStr = JSON.stringify(JS_OBJ);
    wSocketSend(jsonStr);
  };
  const wSocketSend = (jsonStr) => {
    console.log("wSocketSend jsonStr > ", jsonStr);
    wSocket.send(jsonStr);
  };
  const clearScreen = () => {
    console.log("call clearScreen");
    formik.resetForm();
    // setFiles2([]);
    setCustomerImage([]);
  };

  const putToScreen = async (jsObj) => {
    // console.log("jsObj > ", jsObj);
    try {
      let prefix = "";
      let fullName = "";
      let address = "";
      const IDNum = jsObj.ID_Number;
      const IDText = jsObj.ID_Text;
      const IDPhoto = jsObj.ID_Photo;
      const status = jsObj.Status;
      if (status == 0) {
        if (IDText != "") {
          const NIDData = IDText.split("#");
          const prefix_T = NIDData[1];
          const firstName_T = NIDData[2];
          const lastName_T = NIDData[4];
          prefix = prefix_T;
          fullName = firstName_T + " " + lastName_T;
          address =
            NIDData[9] +
            " " +
            NIDData[10] +
            " " +
            NIDData[11] +
            " " +
            NIDData[12] +
            " " +
            NIDData[13] +
            "\r\n" +
            NIDData[14] +
            " " +
            NIDData[15] +
            " " +
            NIDData[16];
        }
      }
      // 2. อัปเดต Formik values พร้อมกัน
      await Promise.all([
        formik.setFieldValue("prefix", prefix),
        formik.setFieldValue("idCardNumber", IDNum),
        formik.setFieldValue("fullName", fullName),
        formik.setFieldValue("address", address),
      ]);

      // 3. อัปเดต touched หลังจาก values ถูกตั้งค่า
      formik.setTouched({
        ...formik.touched,
        idCardNumber: true,
        fullName: true,
        address: true,
      });

      // 4. ตรวจสอบ validation ใหม่
      setTimeout(() => {
        formik.validateForm();
      }, 50);

      // 5. อัปเดตรูปภาพ (ถ้ามี)
      if (IDPhoto != null) {
        const photoUrl = `data:image/jpeg;base64,${IDPhoto}`;
        const fileName = `id_card_${Date.now()}.jpg`;
        const idCardFile = dataURLtoFile(photoUrl, fileName);
        const nidCardFile = {
          file: idCardFile,
          url: photoUrl,
          name: fileName,
          status: "complete",
          isFromCard: true,
        };
        console.log("nidCardFile > ", nidCardFile);
        // setFiles2([nidCardFile]);
        setCustomerImage([nidCardFile]);
      }
    } catch (error) {
      console.error("Error in putToScreen:", error);
    }
  };

  const onGetMessage = (jsonString) => {
    const msgObj = JSON.parse(jsonString);
    console.log("💬 onGetMessage:", msgObj.Message, "Status:", msgObj.Status);

    if (msgObj.Message === "AgentStatusE") {
      if (msgObj.Status === 1) {
        setAutoReadOptions();
      } else {
        console.warn("Agent error:", msgObj.Status);
      }
    }

    if (msgObj.Message === "CardStatusChangeE") {
      console.log("CardStatusChangeE received:", msgObj.Status);

      // ล้างเฉพาะตอนถอดบัตร (-16 หรือ -15)
      if (msgObj.Status === -16 || msgObj.Status === -15) {
        console.log("🧾 Card removed - clear form and image");
        clearScreen();
      } else if (msgObj.Status === 1 || msgObj.Status === 0) {
        // ห้ามล้างภาพตอนเสียบบัตรหรือกำลังอ่าน
        console.log("Card inserted or reading, ignore clear.");
      } else {
        console.log("Unhandled card status:", msgObj.Status);
      }
    }

    if (msgObj.Message === "AutoReadIDCardE") {
      console.log("AutoReadIDCardE -> call putToScreen");
      putToScreen(msgObj);
    }
  };

  useEffect(() => {
    if (open) {
      wSocket = new WebSocket("ws://localhost:14820/TDKWAgent");
      wSocket.onopen = () => {
        console.log("WebSocket opened");
        setAutoReadOptions();
      };

      wSocket.onmessage = function (evt) {
        // console.log("RAW >>>", evt.data);
        onGetMessage(evt.data);
      };

      wSocket.onerror = (error) => {
        console.log("WebSocket: error", error);
      };

      return () => {
        wSocket.close();
        console.log("WebSocket closed");
      };
    }
  }, [open]);
  // end card reader

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isSuccess) {
          fetchData();
        }
        onClose();
      }}
      maxWidth={"md"}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          padding: 2,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h4">
          {activeStep === 1 ? "ตัวอย่างแบบฟอร์มรายการขายฝาก" : "รายการขายฝากรถ"}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: 2,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.grey[300],
            borderRadius: 4,
          },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <input
            type="checkbox"
            class="form-check-input mx-3"
            id="chkAutoRead"
            onclick="setAutoReadOptions();"
            checked="checked"
            style={{ display: "none" }}
          ></input>
          <input
            type="checkbox"
            class="form-check-input"
            id="chkAutoNIDNumber"
            onclick="setAutoReadOptions();"
            checked="checked"
            style={{ display: "none" }}
          ></input>
          <input
            type="checkbox"
            class="form-check-input"
            id="chkAutoNIDText"
            checked="checked"
            onclick="setAutoReadOptions();"
            style={{ display: "none" }}
          ></input>
          <input
            type="checkbox"
            class="form-check-input"
            id="chkAutoNIDPhoto"
            checked="checked"
            onclick="setAutoReadOptions();"
            style={{ display: "none" }}
          ></input>
          {activeStep === 0 ? (
            <>
              <RenderFieldsCustomers
                formik={formik}
                setCustomerImage={setCustomerImage}
                // images={customerImage}
                images={(customerImage || []).map((img) => ({
                  url:
                    img?.url ||
                    (img?.file instanceof File
                      ? URL.createObjectURL(img.file)
                      : null),
                  name: img?.name || "",
                }))}
                disabled={isEdit}
              />
              <RenderFieldsCars
                sx={{ mt: 2 }}
                formik={formik}
                setCarImage={setCarImages}
                images={carImages || []}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Typography variant="h5">เงื่อนไขการขายฝาก</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item sm={2}>
                        <Typography variant="body1">ระยะเวลา</Typography>
                      </Grid>
                      <Grid item sm={9}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={formik.values.total_month}
                          onChange={formik.handleChange}
                          name="total_month"
                          type="number"
                          disabled={isEdit}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "white",
                              height: "40px",
                            },
                          }}
                          inputProps={{
                            min: 0,
                          }}
                          onKeyPress={(event) => {
                            // Allow only numeric input (0-9)
                            const charCode = event.which
                              ? event.which
                              : event.keyCode;
                            if (charCode < 48 || charCode > 57) {
                              event.preventDefault();
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Typography variant="body1">เดือน</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item sm={2}>
                        <Typography variant="body1">อัตราดอกเบี้ย</Typography>
                      </Grid>
                      <Grid item sm={9}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          value={formik.values.interest_rate}
                          onChange={formik.handleChange}
                          name="interest_rate"
                          disabled={isEdit}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "white",
                              height: "40px",
                            },
                          }}
                          inputProps={{
                            min: 0,
                          }}
                          onKeyPress={(event) => {
                            // Allow only numeric input (0-9) and 2 decimal points
                            const charCode = event.which
                              ? event.which
                              : event.keyCode;
                            if (
                              charCode !== 46 && // Allow dot
                              (charCode < 48 || charCode > 57)
                            ) {
                              event.preventDefault();
                            } else {
                              // ตรวจสอบทศนิยมไม่เกิน 2 ตำแหน่ง
                              const inputValue = event.target.value;
                              const regex = /^\d*\.?\d{0,2}$/;
                              if (!regex.test(inputValue + event.key)) {
                                // ตัดให้เหลือแค่ 2 ตำแหน่งทศนิยม
                                event.preventDefault();
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item sm={1}>
                        <Typography variant="body1">% ต่อปี</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            <Grid container spacing={2}>
              {/* Left Side: PDF Preview (Scrollable) */}
              <Grid
                item
                xs={9}
                sx={{
                  backgroundColor: "#f0f0f0",
                  padding: "20px",
                  height: "75vh",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  "& h6": { fontSize: 16, fontWeight: "bold" },
                  "& .MuiTypography-root": {
                    fontFamily: "Sarabun, sans-serif",
                    fontSize: 10,
                  },
                }}
              >
                <Box
                  sx={{
                    mt: 2,
                    transform: "scale(0.75)",
                    transformOrigin: "top left", // จุดยึดอยู่มุมบนซ้าย
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: 3,
                      boxShadow: 3,
                      bgcolor: "white",
                      p: 3,
                      width: "794px",
                      height: "559px",
                      position: "relative",
                      mb: 2,
                    }}
                  >
                    <ReceiptHeader
                      data={{
                        fullName: formik.values.fullName,
                        idCardNumber: formik.values.idCardNumber,
                        phoneNumber: formik.values.phoneNumber,
                        address: formik.values.address,
                      }}
                      label="ใบขายฝาก"
                      date={[
                        {
                          label: "วันครบกำหนด",
                          value: new Date(
                            new Date().setMonth(
                              new Date().getMonth() + formik.values.total_month
                            )
                          ).toLocaleDateString("th-TH", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          }),
                        },
                      ]}
                    />
                    <Typography>
                      1. ผู้ขายฝากได้นำทรัพย์สินปรากฎตามรายการดังนี้
                    </Typography>
                    <TableProductReceipt
                      columns={[
                        {
                          label: "รายการขายฝาก",
                          key: "car_name",
                          render: (_, product) => (
                            <>
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  display: "flex",
                                }}
                              >
                                1. {product.car_name || "-"}
                              </Typography>
                              <Typography>
                                {product.license_plate
                                  ? `(${product.license_plate} ${product.license_province})`
                                  : "-"}
                              </Typography>
                            </>
                          ),
                        },
                        { label: "รหัส1", key: "car_code" },
                        { label: "รหัส2", key: "car_code_com" },
                        {
                          label: "ราคา/หน่วย",
                          key: "car_price",
                          render: (_, product) =>
                            product.car_price
                              ? formatCurrency2Digit(product.car_price)
                              : "-",
                        },
                        { label: "จำนวน", key: "total_amount" },
                        { label: "ส่วนลด", key: "discount_price" },
                        {
                          label: "มูลค่ารวม",
                          key: "total_price",
                          // Custom render function to calculate total sell price
                          render: (_, product) => (
                            <Typography sx={{ fontWeight: "bold" }}>
                              {formatCurrency2Digit(
                                parseFloat(product.car_price || 0) *
                                  parseFloat(product.total_amount || 1)
                              ) + " บาท"}
                            </Typography>
                          ),
                        },
                      ]}
                      products={[
                        {
                          car_name: formik.values.car_name,
                          license_plate: formik.values.license_plate,
                          license_province: formik.values.license_province,
                          car_price: formik.values.car_price,
                          car_code: "PC00012345XXXX",
                          car_code_com: "-",
                          total_amount: 1,
                          discount_price: 0,
                        },
                      ]}
                    />
                    <ReceiptFooter index={0} length={1} />
                  </Box>
                  <Box
                    sx={{
                      borderRadius: 3,
                      boxShadow: 3,
                      bgcolor: "white",
                      p: 3,
                      width: "794px",
                      height: "559px",
                      position: "relative",
                      mb: 2,
                    }}
                  >
                    <ReceiptHeader
                      data={{
                        fullName: formik.values.fullName,
                        idCardNumber: formik.values.idCardNumber,
                        phoneNumber: formik.values.phoneNumber,
                        address: formik.values.address,
                      }}
                      label="ใบขายฝาก"
                      date={[
                        {
                          label: "วันครบกำหนด",
                          value: new Date(
                            new Date().setMonth(
                              new Date().getMonth() + formik.values.total_month
                            )
                          ).toLocaleDateString("th-TH", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          }),
                        },
                      ]}
                    />
                    <Typography>
                      2. ผู้ขายฝากยอมให้คิดดอกเบี้ยอัตราร้อยละ 5% ต่อปี
                      ตามจำนวนเงินที่ขายฝากไว้
                      นับตั้งแต่วันที่ทำหน้งสือนี้เป็นต้นไป
                      จนกว่าจะมาไถ่ถอนคืนในวันที่ 31 เดือน ธันวาคม ปี 2567
                    </Typography>
                    <Typography>
                      3. ผู้ขายฝากรับรองว่าทรัพย์สินที่นำมาขายฝาก
                      เป็นทรัพย์สินของผู้ขายฝากจริง
                    </Typography>
                    <Typography>
                      4. ผู้รับฝากตกลงว่าจะไม่นำทรัพย์ที่ขายฝากออกจำหน่าย
                      ถ้าผู้รับฝาก จำหน่ายทรัพย์สินที่ขายฝากนั้น
                      โดยฝ่าผืนสัญญาข้อนี้
                      ผู้รับฝากต้องรับผิดต่อผู้ขายฝากในความเสียหายใดๆ
                      ที่อันเกิดแต่การนั้น 
                    </Typography>

                    {/* Summary Information */}
                    <Grid
                      container
                      xs={12}
                      sx={{
                        mt: 1,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Grid item xs={6}>
                        <Grid container justifyContent="flex-end">
                          <Grid item xs={6}>
                            <Typography align="right">
                              อัตราดอกเบี้ย/เดือน
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography align="right">
                              {formik.values.interest_rate
                                ? formik.values.interest_rate
                                : 0}
                              %
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid container justifyContent="flex-end">
                          <Grid item xs={6}>
                            <Typography align="right">ระยะเวลา</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography align="right">
                              {formik.values.total_month ?? 0} เดือน
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid container justifyContent="flex-end">
                          <Grid item xs={6}>
                            <Typography align="right">
                              ดอกเบี้ย/เดือน
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography align="right">
                              {formatCurrency2Digit(
                                formik.values.car_price *
                                  (formik.values.interest_rate / 100)
                              )}{" "}
                              บาท
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      xs={12}
                      sx={{
                        mt: 1,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Grid item xs={6}>
                        <Grid container justifyContent="flex-end">
                          <Grid item xs={6}>
                            <Typography
                              align="right"
                              style={{ fontWeight: "bold" }}
                            >
                              รวมจำนวนเงินรายการซื้อทั้งสิ้น
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              align="right"
                              style={{ fontSize: "10px", fontWeight: "bold" }}
                            >
                              {formatCurrency2Digit(formik.values.car_price)}{" "}
                              บาท
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    {/* Footer */}
                    <Box
                      sx={{
                        mt: 5,
                        border: "1px solid #00BB9F",
                        borderRadius: "12px",
                        width: "100%",
                      }}
                    >
                      <Grid container sx={{ height: "100%" }}>
                        {/* Column 1 */}
                        <Grid
                          item
                          xs={4}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRight: "1px solid #00BB9F",
                            padding: "10px",
                          }}
                        >
                          {/* Top Divider Line */}
                          <Box
                            sx={{
                              width: "100%",
                              borderTop: "1px solid #B0B0B0",
                              mb: 1,
                              mt: 5,
                            }}
                          />
                          <Typography
                            align="center"
                            style={{ fontSize: "12px" }}
                          >
                            ลูกค้า (ผู้ขาย)
                          </Typography>
                          <Typography align="center">
                            {formatDateMonthYear(new Date())}
                          </Typography>
                        </Grid>

                        {/* Column 2 */}
                        <Grid
                          item
                          xs={4}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRight: "1px solid #00BB9F",
                            padding: "10px",
                            height: "100%", // Ensure the column takes full height
                          }}
                        >
                          {/* Top Divider Line */}
                          <Box
                            sx={{
                              width: "100%",
                              borderTop: "1px solid #B0B0B0",
                              mb: 1,
                              mt: 5,
                            }}
                          />
                          <Typography
                            align="center"
                            style={{ fontSize: "12px" }}
                          >
                            ร้านค้า (ซื้อ)
                          </Typography>
                          <Typography align="center">
                            {formatDateMonthYear(new Date())}
                          </Typography>
                        </Grid>

                        {/* Column 3 */}
                        <Grid
                          item
                          xs={4}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "10px",
                            height: "100%", // Ensure the column takes full height
                          }}
                        >
                          {/* Top Divider Line */}
                          <Box
                            sx={{
                              width: "100%",
                              borderTop: "1px solid #B0B0B0",
                              mb: 1,
                              mt: 5,
                            }}
                          />
                          <Typography
                            align="center"
                            style={{ fontSize: "12px" }}
                          >
                            ผู้อนุมัติ
                          </Typography>
                          <Typography align="center">
                            {formatDateMonthYear(new Date())}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <ReceiptFooter index={1} length={1} />
                  </Box>
                </Box>
              </Grid>

              {/* Right Side: Form Fields */}
              <Grid
                item
                xs={3}
                display={"flex"}
                flexDirection={"column"}
                style={{ padding: "20px" }}
              >
                <GradientShadow isActive={true} style={{ margin: "10px" }}>
                  ใบขายฝาก
                </GradientShadow>
              </Grid>
            </Grid>
          )}
        </form>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: !isSuccess ? "space-between" : "flex-end",
          borderTop: `1px solid ${colors.grey[300]}`,
        }}
      >
        {!isSuccess && (
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? onClose : () => setActiveStep(0)}
            sx={{
              borderRadius: 2,
              width: "auto",
              borderColor: colors.grey[400],
              color: colors.grey[400],
              "&:hover": {
                borderColor: colors.grey[400],
                color: colors.grey[400],
                borderWidth: 2,
                bgcolor: "transparent",
              },
            }}
          >
            <NavigateBefore
              sx={{
                width: 30,
                height: 30,
              }}
            />
          </Button>
        )}

        {isEdit || activeStep === 1 ? (
          <Box
            sx={{
              display: "flex",
              direction: "row",
              gap: 2,
            }}
          >
            {!isSuccess ? (
              <GradientButton
                onClick={formik.handleSubmit}
                sx={{
                  width: "100px",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                type="submit"
                disabled={loading}
                loading={loading}
                startIcon={
                  loading ? (
                    <CircularProgress color="inherit" size={15} thickness={5} />
                  ) : null
                }
              >
                <Typography sx={{ fontSize: "16px" }}>บันทึก</Typography>
              </GradientButton>
            ) : (
              <GradientButtonOutline
                onClick={() => {
                  if (isSuccess) {
                    fetchData();
                  }
                  onClose();
                }}
                sx={{
                  width: "150px",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                endIcon={<CheckCircle />}
              >
                เสร็จสิ้น
              </GradientButtonOutline>
            )}
            {activeStep === 1 && (
              <Tooltip
                title={
                  <Typography
                    sx={{
                      fontSize: "14px",
                    }}
                  >
                    กรุณากดบันทึกก่อนพิมพ์
                  </Typography>
                }
                arrow={true}
                placement="top"
                disableHoverListener={isSuccess} // ปิด tooltip ถ้า isSuccess = true
                disableFocusListener={isSuccess}
              >
                <span>
                  <GradientButtonOutline
                    disabled={!isSuccess}
                    sx={{
                      width: "100px",
                      border: "1px solid #ccc",
                    }}
                  >
                    พิมพ์
                  </GradientButtonOutline>
                </span>
              </Tooltip>
            )}
          </Box>
        ) : (
          <GradientButtonOutline
            onClick={() => setActiveStep(1)}
            sx={{
              width: "auto",
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&.Mui-disabled": {
                borderColor: colors.grey[400],
                color: colors.grey[400],
              },
            }}
            disabled={!formik.isValid || !formik.dirty || loading}
          >
            <Typography sx={{ fontSize: "16px" }}>ดูตัวอย่าง</Typography>
          </GradientButtonOutline>
        )}
      </DialogActions>
    </Dialog>
  );
};
export default ModalPawnCar;
